import http from './api'
import { Formula1Race, Formula1Prediction, Formula1RoomSettings, Formula1PlayerPoints } from '../types/formula1'

class Formula1Service {
  // Races
  async getRaces(filters?: {
    status?: 'upcoming' | 'in-progress' | 'completed'
    day?: number
    limit?: number
    offset?: number
  }): Promise<Formula1Race[]> {
    const params = new URLSearchParams()
    if (filters?.status) params.append('status', filters.status)
    if (filters?.day) params.append('day', filters.day.toString())
    if (filters?.limit) params.append('limit', filters.limit.toString())
    if (filters?.offset) params.append('offset', filters.offset.toString())

    const queryString = params.toString()
    const response = await http.get(`/formula1-races${queryString ? `?${queryString}` : ''}`)
    // Handle both response formats: { races: [...] } or direct array
    const racesData = response.data?.races || response.data
    return Array.isArray(racesData) ? racesData : []
  }

  async getRace(raceId: string): Promise<Formula1Race> {
    const response = await http.get(`/formula1-races/${raceId}`)
    return response.data
  }

  /** Admin: update race actualResults and/or status (PATCH /formula1-races/:id, requires AdminGuard). */
  async updateRace(
    raceId: string,
    data: { actualResults?: Record<string, number>; status?: 'upcoming' | 'in-progress' | 'completed' }
  ): Promise<Formula1Race> {
    const response = await http.patch<Formula1Race>(`/formula1-races/${raceId}`, data)
    return response.data
  }

  // Predictions
  async createPrediction(data: {
    roomId: string
    raceId: string
    predictedPositions: Record<string, number>
  }): Promise<Formula1Prediction> {
    const response = await http.post('/formula1-predictions', data)
    return response.data
  }

  async updatePrediction(
    roomId: string,
    raceId: string,
    data: {
      predictedPositions?: Record<string, number>
    }
  ): Promise<Formula1Prediction> {
    const response = await http.patch(`/formula1-predictions/room/${roomId}/race/${raceId}`, data)
    return response.data
  }

  async getPrediction(roomId: string, raceId: string): Promise<Formula1Prediction | null> {
    try {
      const response = await http.get(`/formula1-predictions/room/${roomId}/race/${raceId}`)
      // Handle both response formats: { prediction: {...} } or direct object
      return response.data?.prediction || response.data || null
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null
      }
      throw error
    }
  }

  async getRoomPredictions(roomId: string): Promise<Formula1Prediction[]> {
    const response = await http.get(`/formula1-predictions/room/${roomId}`)
    return response.data
  }

  async getPlayerPredictions(roomId: string, playerId: string): Promise<Formula1Prediction[]> {
    const response = await http.get(`/formula1-predictions/room/${roomId}/player/${playerId}`)
    // Handle both response formats: { predictions: [...] } or direct array
    const predictionsData = response.data?.predictions || response.data
    return Array.isArray(predictionsData) ? predictionsData : []
  }

  // Room Settings
  async getRoomSettings(roomId: string): Promise<Formula1RoomSettings> {
    const response = await http.get(`/formula1-room-settings/room/${roomId}`)
    return response.data
  }

  async updateRoomSettings(roomId: string, data: Partial<Formula1RoomSettings>): Promise<Formula1RoomSettings> {
    const response = await http.patch(`/formula1-room-settings/room/${roomId}`, data)
    return response.data
  }

  // Player Points & Leaderboard
  async getLeaderboard(roomId: string): Promise<Formula1PlayerPoints[]> {
    const response = await http.get(`/formula1-player-points/room/${roomId}/leaderboard`)
    // Backend may return { leaderboard: [...] } or just array
    return response.data?.leaderboard || response.data || []
  }

  async getPlayerStats(roomId: string, playerId: string): Promise<Formula1PlayerPoints> {
    const response = await http.get(`/formula1-player-points/room/${roomId}/player/${playerId}`)
    return response.data
  }
}

const formula1Service = new Formula1Service()
export default formula1Service

